import { useEffect, useState } from "react";
import { proApi } from "@/lib/proApi";
import { API } from "@/lib/api";
import { QrCode, Copy, Download, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ProLinks() {
  const [items, setItems] = useState(null);
  const [qr, setQr] = useState(null);

  useEffect(() => {
    proApi.get("/pro/shortlinks").then((r) => setItems(r.data.items || [])).catch(() => toast.error("Laden fehlgeschlagen"));
  }, []);

  const copy = async (txt) => { try { await navigator.clipboard.writeText(txt); toast.success("Link kopiert"); } catch { } };
  const downloadQr = async (slug) => {
    try {
      const r = await fetch(`${API}/shortlink/${encodeURIComponent(slug)}/qr`);
      const blob = await r.blob(); const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `QR-${slug}.png`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Download fehlgeschlagen"); }
  };

  return (
    <div data-testid="pro-links-page">
      <p className="font-mono-lab text-[10px] tracking-[0.25em] text-[#ff0033] uppercase">// KURZLINKS & QR-CODES</p>
      <h1 className="font-heading text-3xl md:text-4xl uppercase tracking-tight mt-1">QR-Codes</h1>
      <p className="text-neutral-400 text-sm mt-2 max-w-2xl">Alle erstellten Kurzlinks und QR-Codes der Academy – für Poster, Flyer und Aktionen. QR anzeigen, Link kopieren oder PNG herunterladen.</p>

      {!items ? (
        <div className="flex items-center gap-2 text-neutral-400 mt-10"><Loader2 size={16} className="animate-spin" /> Laden…</div>
      ) : items.length === 0 ? (
        <p className="text-neutral-400 text-sm mt-10">Noch keine Kurzlinks vorhanden.</p>
      ) : (
        <div className="mt-6 overflow-x-auto border border-white/10">
          <table className="w-full text-sm min-w-[700px]">
            <thead><tr className="bg-[#111] border-b border-white/10 text-neutral-500 font-mono-lab text-[10px] uppercase tracking-widest text-left">
              <th className="p-3">Kurzlink</th><th className="p-3">Ziel</th><th className="p-3">Klicks</th><th className="p-3">Status</th><th className="p-3 text-right">QR</th>
            </tr></thead>
            <tbody>
              {items.map((d) => (
                <tr key={d.id} className="border-b border-white/5" data-testid={`pro-link-row-${d.slug}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-2 text-white font-mono-lab">/r/{d.slug}
                      <button onClick={() => copy(d.short_url)} className="text-neutral-500 hover:text-[#ff0033]" title="Link kopieren" data-testid={`pro-link-copy-${d.slug}`}><Copy size={13} /></button></div>
                    {d.label && <div className="text-neutral-500 text-xs mt-0.5">{d.label}</div>}
                  </td>
                  <td className="p-3 text-neutral-400 max-w-[280px] truncate">{d.target}</td>
                  <td className="p-3 text-neutral-400">{d.clicks || 0}</td>
                  <td className="p-3"><span className={`inline-block font-mono-lab text-[10px] uppercase px-2 py-1 border ${d.active ? "text-emerald-400 border-emerald-500/40" : "text-neutral-500 border-white/15"}`}>{d.active ? "Aktiv" : "Inaktiv"}</span></td>
                  <td className="p-3 text-right">
                    <button onClick={() => setQr(d)} data-testid={`pro-link-qr-${d.slug}`} className="inline-flex items-center gap-1 border border-[#ff0033]/50 text-[#ff0033] font-mono-lab text-[10px] uppercase px-2.5 py-1.5 hover:bg-[#ff0033]/10 transition-colors"><QrCode size={12} /> QR anzeigen</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {qr && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4" data-testid="pro-link-qr-modal">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setQr(null)} />
          <div className="relative bg-[#0A0A0A] border border-white/10 w-full max-w-sm p-6 text-center">
            <div className="font-mono-lab text-[10px] tracking-widest text-neutral-500 uppercase">Dynamischer QR-Code</div>
            <div className="font-heading text-xl text-white tracking-widest mt-1 mb-4">/r/{qr.slug}</div>
            <div className="bg-white p-3 inline-block mb-4">
              <img src={`${API}/shortlink/${encodeURIComponent(qr.slug)}/qr`} alt={`QR ${qr.slug}`} className="w-56 h-56" data-testid="pro-link-qr-image" />
            </div>
            <div className="flex items-center gap-2 bg-[#111] border border-white/10 px-3 py-2 mb-2">
              <ExternalLink size={13} className="text-[#ff0033] shrink-0" /><span className="text-neutral-300 text-xs break-all text-left flex-1">{qr.short_url}</span>
            </div>
            <div className="text-neutral-500 text-[11px] mb-4">→ leitet aktuell auf: <span className="text-neutral-300 break-all">{qr.target}</span></div>
            <div className="flex gap-2">
              <button onClick={() => downloadQr(qr.slug)} data-testid="pro-link-qr-download" className="flex-1 flex items-center justify-center gap-2 bg-[#ff0033] text-white font-mono-lab text-[11px] uppercase tracking-widest py-2.5 hover:bg-[#cc0029] transition-colors"><Download size={14} /> PNG</button>
              <button onClick={() => copy(qr.short_url)} data-testid="pro-link-qr-copy" className="flex-1 flex items-center justify-center gap-2 border border-white/15 text-neutral-300 font-mono-lab text-[11px] uppercase tracking-widest py-2.5 hover:border-[#ff0033] hover:text-white transition-colors"><Copy size={14} /> Link</button>
            </div>
            <button onClick={() => setQr(null)} className="mt-4 text-neutral-500 text-xs hover:text-white">Schließen</button>
          </div>
        </div>
      )}
    </div>
  );
}
